import * as React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { LoadedSessionContext } from 'supertokens-auth-react/lib/build/recipe/session/types';
import { selectUser } from 'gossamer-front-end';
import { postChameleonRequest } from './ChameleonLogin';

interface ChameleonSessionInfo {
  staffUserId: string;
  staffEmail: string;
  staffName: string;
}

/**
 * Shown on every page while a staff user is logged in as another user, with a button to return to their own account
 */
export const ChameleonBanner = (): React.JSX.Element | null => {
  const session = useSessionContext();
  const user = useSelector(selectUser);
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const chameleon = session.loading
    ? undefined
    : ((session as LoadedSessionContext).accessTokenPayload?.chameleon as ChameleonSessionInfo | undefined);

  if (!chameleon) {
    return null;
  }

  const returnToOwnAccount = async () => {
    setLoading(true);
    try {
      await postChameleonRequest('chameleon-logout');
      window.location.href = '/admin';
    } catch (err) {
      setLoading(false);
      window.alert((err as Error).message || 'Failed to return to your account - please log out and in again');
    }
  };

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2000,
        maxWidth: 'calc(100vw - 32px)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 16px',
        borderRadius: 8,
        background: '#b42318',
        color: 'white',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
      }}
    >
      <span>
        Logged in as <strong>{user?.name || user?.email || 'another user'}</strong> by{' '}
        {chameleon.staffName || chameleon.staffEmail}
      </span>
      <Button size="sm" variant="light" onClick={returnToOwnAccount} disabled={isLoading}>
        {isLoading && <Spinner as="span" animation="border" size="sm" aria-hidden="true" className="me-2" />}
        Return to my account
      </Button>
    </div>
  );
};
